import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, LocaleSchema, PhoneSchema, nullableText, optionalText, requiredText, } from './common.js';
import { CustomerTypeSchema, RoleScopeSchema } from './enums.js';
import { PermissionCodeSchema } from './permissions.js';
/* ============================ Foydalanuvchi ============================ */
export const UserResponseSchema = z.object({
    id: IdSchema,
    telegramId: z.string().nullable(), // BigInt — transportda string
    phone: nullableText(20),
    firstName: nullableText(80),
    lastName: nullableText(80),
    username: nullableText(64),
    locale: LocaleSchema,
    customerType: CustomerTypeSchema,
    isStaff: z.boolean(),
    isActive: z.boolean(),
    lastSeenAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
});
/** Joriy foydalanuvchi — ruxsatlar bilan. Klient UI ni shularga qarab yashiradi. */
export const MeResponseSchema = UserResponseSchema.extend({
    roles: z.array(z.string()),
    permissions: z.array(PermissionCodeSchema),
});
export const UpdateProfileSchema = z
    .object({
    firstName: optionalText(80),
    lastName: optionalText(80),
    phone: PhoneSchema.optional(),
    locale: LocaleSchema.optional(),
})
    .strict();
/* =============================== Rollar ================================ */
export const RoleResponseSchema = z.object({
    id: IdSchema,
    code: z.string(),
    nameUz: z.string(),
    nameRu: nullableText(64),
    description: nullableText(255),
    isSystem: z.boolean(),
    /**
     * Do'kon ko'lami (TASK-007-A, D05). `SHOP` rol faqat do'kon bilan
     * beriladi — admin forma do'kon maydonini shunga qarab ko'rsatadi.
     * `null` — tasniflanmagan, berib bo'lmaydi (TASK-007-B).
     */
    scope: RoleScopeSchema.nullable(),
    permissions: z.array(PermissionCodeSchema),
});
export const UpdateRolePermissionsSchema = z
    .object({ permissions: z.array(PermissionCodeSchema) })
    .strict();
export const AssignRoleSchema = z
    .object({
    roleId: IdSchema,
    /** Kuryer va do'kon xodimi bitta do'konga biriktiriladi. */
    shopId: IdSchema.optional(),
})
    .strict();
/* ============================ Xodim yaratish =========================== */
/**
 * Xodim o'z-o'zidan ro'yxatdan o'tmaydi — uni Owner yaratadi
 * (docs/06-OCHIQ-SAVOLLAR.md → 10-savol).
 */
const StaffPasswordSchema = z
    .string()
    .min(12, "Parol kamida 12 belgi bo'lishi kerak")
    .max(128)
    .regex(/[a-z]/, "Kichik harf bo'lishi kerak")
    .regex(/[A-Z]/, "Katta harf bo'lishi kerak")
    .regex(/\d/, "Raqam bo'lishi kerak");
export const CreateStaffSchema = z
    .object({
    staffLogin: z
        .string()
        .trim()
        .min(3)
        .max(64)
        .regex(/^[a-z0-9_]+$/, 'Login faqat kichik harf, raqam va pastki chiziq'),
    password: StaffPasswordSchema,
    firstName: requiredText(80),
    lastName: optionalText(80),
    phone: PhoneSchema.optional(),
    locale: LocaleSchema.default('UZ'),
    roleIds: z.array(IdSchema).min(1, 'Kamida bitta rol tanlanishi kerak'),
    shopId: IdSchema.optional(),
})
    .strict();
/**
 * Xodimlar ro'yxati — ROLI bilan.
 *
 * Rolsiz ro'yxat "kim nima qila oladi" savoliga javob bermasdi, va
 * aynan shu savol audit jurnalini ochgan odamda paydo bo'ladi.
 */
export const StaffRoleSchema = z.object({
    roleId: IdSchema,
    code: z.string(),
    nameUz: z.string(),
    /** Kuryer va do'kon xodimi bitta do'konga biriktiriladi. */
    shopId: IdSchema.nullable(),
    shopNameUz: z.string().nullable(),
});
export const StaffListItemSchema = UserResponseSchema.extend({
    staffLogin: z.string().nullable(),
    roles: z.array(StaffRoleSchema),
});
export const ListStaffQuerySchema = z
    .object({
    /** Faolsizlar YASHIRILMAYDI: ular kimligini ko'rish kerak. */
    q: z.string().trim().max(64).optional(),
})
    .strict();
export const ChangePasswordSchema = z
    .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: StaffPasswordSchema,
})
    .strict()
    .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'Yangi parol eskisidan farq qilishi kerak',
    path: ['newPassword'],
});
/* ========================== Autentifikatsiya =========================== */
/**
 * Telegram Mini App kirishi.
 *
 * `initData` — ISHONCHSIZ MATN. Server uni HMAC imzosi va `auth_date`
 * muddati bo'yicha tekshiradi; ichidagi hech bir maydonga tekshiruvdan
 * oldin ishonilmaydi (avtopart-telegram-auth skill).
 */
export const TelegramLoginSchema = z.object({ initData: z.string().min(1).max(4096) }).strict();
/**
 * Telegram ulashgan kontakt — IMZOLANGAN yuklama.
 *
 * Raqam KLIENTDAN ALOHIDA maydon sifatida OLINMAYDI: u holda
 * istalgan odam istalgan raqamni o'ziga biriktira olardi. Telegram
 * bergan yuklama bot tokeni bilan imzolangan va server uni lokal
 * tekshiradi.
 */
export const TelegramContactSchema = z
    .object({ contactData: z.string().min(1).max(4096) })
    .strict();
/** Raqam saqlangandan keyingi javob. */
export const PhoneSavedResponseSchema = z.object({ phone: z.string() });
/** Web kirishi — telefon + SMS OTP. */
export const RequestOtpSchema = z.object({ phone: PhoneSchema }).strict();
export const VerifyOtpSchema = z
    .object({
    phone: PhoneSchema,
    code: z.string().regex(/^\d{6}$/, 'Kod 6 raqamdan iborat'),
})
    .strict();
/**
 * Web kirishi — FAQAT telefon raqami (SMS kodsiz).
 *
 * Bu zaif kirish: raqamni bilgan har kim kira oladi. Shuning uchun server
 * xodim/rolli raqamlarni rad etadi va bu sessiyada buyurtmadagi shaxsiy
 * ma'lumotlar (manzil, to'liq telefon) yashiriladi.
 */
export const PhoneLoginSchema = z.object({ phone: PhoneSchema }).strict();
/** Admin panel kirishi — login + parol, Owner uchun 2FA. */
export const StaffLoginSchema = z
    .object({
    staffLogin: z.string().trim().min(3).max(64),
    password: z.string().min(1).max(128),
    totp: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
})
    .strict();
export const RefreshTokenSchema = z.object({ refreshToken: z.string().min(1).max(1024) }).strict();
/**
 * Token javobi. Rol va ruxsat token ichida SAQLANMAYDI — rol o'zgarganda
 * eski token kuch saqlab qolmasligi uchun ular har so'rovda o'qiladi.
 */
export const AuthTokensResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number().int().positive(),
});
/** OTP so'ralganda foydalanuvchi bor-yo'qligi OSHKOR QILINMAYDI. */
export const RequestOtpResponseSchema = z.object({
    retryAfterSeconds: z.number().int().nonnegative(),
});
//# sourceMappingURL=identity.js.map